// Port of src/app/services/youtube/youtube-playlist.service.ts.
// NOTE: on error, createYoutubePlaylist RESOLVES with `{ error: '...' }`
// (Angular's catchError → handleError) — it does not reject. Callers check
// `result.error`.

import { http } from '../http';
import { environment } from '../../config/environment';
import type { PlaylistReturnObject } from '../../interfaces/playlist-return-object';
import { refreshAccessToken, handleError } from './youtube-base';

const playlistItemsUrl = 'https://www.googleapis.com/youtube/v3/playlistItems';
const playlistApiUrl = 'https://www.googleapis.com/youtube/v3/playlists';

export async function createYoutubePlaylist(
  youtubeIds: string[],
  playlistName: string,
  playlistId?: string
): Promise<PlaylistReturnObject> {
  try {
    await refreshAccessToken();

    if (playlistId) {
      return await updatePlaylistTracks(playlistId, youtubeIds);
    } else {
      return await createPlaylistAndAddTracks(playlistName, youtubeIds);
    }
  } catch (error) {
    return handleError(error);
  }
}

async function createPlaylistAndAddTracks(playlistName: string, youtubeIds: string[]): Promise<PlaylistReturnObject> {
  try {
    const playlistId = await createPlaylist(playlistName);
    await addTracksToPlaylist(playlistId, youtubeIds);
    return {
      link: generatePlaylistLink(playlistId),
      playlistId: playlistId,
    };
  } catch (error) {
    console.error('Error creating playlist and adding tracks:', error);
    throw error;
  }
}

function generatePlaylistLink(playlistId: string): string {
  return `https://www.youtube.com/playlist?list=${playlistId}`;
}

async function createPlaylist(playlistName: string): Promise<string> {
  const body = {
    snippet: {
      title: playlistName,
      description: 'Playlist created via My YouTube App', // You can customize this
      privacyStatus: 'public' // or 'private', 'unlisted'
    }
  };

  try {
    const res = await http.post<{ id: string }>(playlistApiUrl, body, {
      params: {
        part: 'snippet',
        key: environment.youtubeApiKey
      }
    });
    return res.data.id;
  } catch (error) {
    console.error('Error creating playlist:', error);
    throw error;
  }
}

async function updatePlaylistTracks(playlistId: string, youtubeIds: string[]): Promise<PlaylistReturnObject> {
  await deletePlaylistTracks(playlistId);
  await addTracksToPlaylist(playlistId, youtubeIds);
  return {
    link: generatePlaylistLink(playlistId),
    playlistId: playlistId
  };
}

// Angular: concatMap over items (strictly sequential deletes), collected with toArray().
async function deletePlaylistTracks(playlistId: string): Promise<any[]> {
  const items = await getPlaylistItems(playlistId);
  const responses: any[] = [];
  for (const item of items) {
    responses.push(await deletePlaylistItem(item.id));
  }
  return responses;
}

async function getPlaylistItems(playlistId: string): Promise<any[]> {
  const params = {
    part: 'snippet',
    maxResults: '500', // Adjust as needed
    playlistId: playlistId,
    key: environment.youtubeApiKey // Replace with your YouTube API key
  };

  try {
    const res = await http.get<{ items: any[] }>(playlistItemsUrl, { params });
    return res.data.items || [];
  } catch (error) {
    console.error('Error fetching playlist items:', error);
    return []; // Return an empty array in case of an error
  }
}

async function deletePlaylistItem(playlistItemId: string): Promise<any> {
  const params = {
    id: playlistItemId,
    key: environment.youtubeApiKey
  };

  const res = await http.delete(playlistItemsUrl, { params });
  return res.data;
}

// Angular: mergeMap with a concurrency limit of 10 → small promise pool.
async function addTracksToPlaylist(playlistId: string, youtubeIds: string[]): Promise<any[]> {
  const CONCURRENCY = 10; // This limits the number of concurrent requests
  const results: any[] = new Array(youtubeIds.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < youtubeIds.length) {
      const index = nextIndex++;
      results[index] = await addTrackToPlaylist(playlistId, youtubeIds[index]);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, youtubeIds.length) }, () => worker())
  );

  return results;
}

async function addTrackToPlaylist(playlistId: string, youtubeId: string): Promise<any> {
  const body = {
    snippet: {
      playlistId: playlistId,
      resourceId: {
        kind: 'youtube#video',
        videoId: youtubeId
      }
    }
  };

  const res = await http.post(playlistItemsUrl, body, {
    params: {
      part: 'snippet',
      key: environment.youtubeApiKey
    }
  });
  return res.data;
}
